/**
 * Brazilian document/contact formatting and validation for Super Admin forms.
 * CNPJ DV mirrors saas/utils/validators/cnpj.py (alphanumeric base + numeric DV).
 */

export function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

export function onlyCnpjChars(value) {
  return String(value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

export function maskCnpj(value) {
  const raw = onlyCnpjChars(value);
  const base = raw.slice(0, 12);
  const dv = raw.slice(12, 14).replace(/\D/g, '');
  const cnpj = (base + dv).slice(0, 14);

  if (cnpj.length <= 2) return cnpj;
  if (cnpj.length <= 5) return cnpj.replace(/^(.{2})(.+)/, '$1.$2');
  if (cnpj.length <= 8) return cnpj.replace(/^(.{2})(.{3})(.+)/, '$1.$2.$3');
  if (cnpj.length <= 12) return cnpj.replace(/^(.{2})(.{3})(.{3})(.+)/, '$1.$2.$3/$4');
  return cnpj.replace(/^(.{2})(.{3})(.{3})(.{4})(\d{1,2})$/, '$1.$2.$3/$4-$5');
}

export function maskPhone(value) {
  const digits = onlyDigits(value).slice(0, 11);
  const size = digits.length;
  if (size === 0) return '';
  if (size <= 2) return `(${digits}`;
  if (size <= 6) return digits.replace(/(\d{2})(\d{0,4})/, '($1) $2');
  if (size <= 10) return digits.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
  return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
}

export function maskCep(value) {
  const digits = onlyDigits(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return digits.replace(/(\d{5})(\d{0,3})/, '$1-$2');
}

export function maskUf(value) {
  return String(value || '')
    .replace(/[^a-zA-Z]/g, '')
    .toUpperCase()
    .slice(0, 2);
}

function cnpjCharValue(char) {
  return char.charCodeAt(0) - 48;
}

function calcCnpjDigit(chars, startWeight) {
  let weight = startWeight;
  let total = 0;
  for (const char of chars) {
    total += cnpjCharValue(char) * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  const remainder = total % 11;
  return remainder < 2 ? 0 : 11 - remainder;
}

export function isValidCnpj(value) {
  const cnpj = onlyCnpjChars(value);
  if (cnpj.length !== 14) return false;
  if (!/^\d{2}$/.test(cnpj.slice(12))) return false;

  const base = cnpj.slice(0, 12);
  const dv = cnpj.slice(12);
  const digit1 = calcCnpjDigit(base, 5);
  const digit2 = calcCnpjDigit(base + String(digit1), 6);
  return dv === `${digit1}${digit2}`;
}

export function isValidPhone(value) {
  const digits = onlyDigits(value);
  return digits.length === 10 || digits.length === 11;
}

export function isValidCep(value) {
  return onlyDigits(value).length === 8;
}

export function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export function isValidUf(value) {
  return /^[A-Z]{2}$/.test(maskUf(value));
}
